import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Modal, ModalProps } from '../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { useShareResultsMutation } from '../../../api/useShareResultsMutation';
import { InviteType, validation } from './ShareModal.meta';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { LegalFooter } from './components/LegalFooter/LegalFooter';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';

interface ShareModalProps extends Omit<ModalProps, 'isOpen'> {
  onClose: () => void;
  inviteType: InviteType;
}

export const ShareModal = ({ onClose, inviteType, ...rest }: ShareModalProps) => {
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: { clientName: '', clientEmail: '' },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: shareResults,
    isLoading: isSharingResults,
    error: shareResultsError,
    reset: resetShareResultsMutation,
  } = useShareResultsMutation();

  const onSubmit = handleSubmit(async data => {
    try {
      await shareResults({ ...data, shareType: inviteType });

      setIsSuccess(true);
    } catch (err) {
      console.error(err);
    }
  });

  return (
    <Modal isOpen size="small" {...rest}>
      <ModalHeader />
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        {isSuccess ? (
          <Box
            py={100}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Icon className="far fa-check-circle" fontSize="xl" color="primary.500" />

            <Text fontSize="h5" fontWeight={600} mt="24" textAlign="center">
              {inviteType === InviteType.Compare &&
                "Sent. When they've completed their part, you'll see the results in your BeFi20 comparisons."}

              {inviteType === InviteType.Share &&
                'Thanks for sharing BeFi20. Way to help your friend invest in their financial future.'}
            </Text>
          </Box>
        ) : (
          <>
            {inviteType === InviteType.Compare && (
              <Text fontSize="h3" fontWeight={600} textAlign="center" mt="24">
                Learn how you think and feel about money
              </Text>
            )}

            {inviteType === InviteType.Share && (
              <Text fontSize="h3" fontWeight={600} textAlign="center" mt="24">
                Pass along the behavioral finance wisdom
              </Text>
            )}

            <Text my="16">
              {inviteType === InviteType.Compare &&
                'Discover how your financial values align with those of a partner, family member, or friend. Enter their name and email address below to invite them to find out.'}
              {inviteType === InviteType.Share &&
                'Have someone in your life who could benefit from a greater understanding of how they think and feel about money? Enter their information below to share the BeFi20 assessment.'}
            </Text>

            {shareResultsError && (
              <Alert
                message={shareResultsError.message}
                onRequestClose={resetShareResultsMutation}
                variant="error"
                mb="16"
              />
            )}

            <form onSubmit={onSubmit}>
              <Input
                {...register('clientName')}
                label="Other Person's Name"
                formVariant={errors.clientName ? 'error' : undefined}
                helperText={errors.clientName?.message}
              />

              <Input
                {...register('clientEmail')}
                label="Other Person's Email Address"
                mt="16"
                formVariant={errors.clientEmail ? 'error' : undefined}
                helperText={errors.clientEmail?.message}
              />

              <LegalFooter inviteType={inviteType} mt="16" />

              <Button
                type="submit"
                mt="24"
                css="width: 100%;"
                isLoading={isSharingResults}
                disabled={isSharingResults}
                size="large"
              >
                {inviteType === InviteType.Compare ? 'Send' : 'Share BeFi20'}
              </Button>
            </form>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};
